import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import { getS3FileKey } from "base/utils";
import { LazyLoadComponent } from "widgets/lazyloader";
import { defaultConfig } from "widgets/inputs";
import { Tooltip } from "widgets/feedback";
import { FLOW_TYPE } from "base/constants";
import { filePreviewAPI } from "kfplatform/filePreview";
import { FILEPREVIEW_PLUGINS } from "kfplatform/constants";

import { Base } from "../base";

import styles from "./attachment.css";

import PlusMediumIcon from "./PlusMedium.svg";
import CloseIcon from "./Close.svg";

const ATTACHMENTOPTIONS = {
  imageProps: defaultConfig.attachmentWidgetProps,
  maxCount: defaultConfig.maxCount,
  maxSize: defaultConfig.maxSize,
  fileExtensions: ["*"]
};
export function Attachment(props) {
  const {
    column,
    value,
    pos,
    row,
    setIsFullScreen = () => {},
    isFullScreen,
    customProps,
    formConfig,
    rowIndex
  } = props;

  let viewType = filePreviewAPI.viewType;

  const baseRef = useRef(null);
  const [currentValue, setCurrentValue] = useState(value);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [filePickerOptions, setFilePickerOptions] = useState({});

  useEffect(
    function onDatasetChange() {
      if (value && Array.isArray(value) && value.length > 0) {
        let files = value.map((file) =>
          Object.assign({}, file, {
            preview: file.photos && getS3FileKey(file.photos?.[1]?.key),
            thumbnail: file.photos && getS3FileKey(file.photos?.[2]?.key)
          })
        );
        setCurrentValue(files);
      } else {
        setCurrentValue([]);
      }
    },
    [value, row, pos]
  );

  useEffect(
    function getConfigurations() {
      if (column && column.getValidateOptions) {
        let validate = column.getValidateOptions({ currentValue }) || {};
        setFilePickerOptions({
          ...ATTACHMENTOPTIONS,
          ...validate,
          maxSize: validate.size
        });
      }
    },
    [column, currentValue]
  );

  const removeFile = function removeAttachments(file) {
    let newValue = currentValue.filter((f) => f.id !== file.id);
    setCurrentValue(newValue);
    baseRef.current.startEditing();
    baseRef.current.dataChanged(newValue.length > 0 ? newValue : null);
    baseRef.current.dataCompleted();
  };

  return (
    <Base {...props} ref={baseRef} onEditing={(e) => {}} type="multiselect">
      <div className={styles.attachment}>
        {!column.readOnly && (
          <button
            className={styles.add}
            onClick={() => {
              setShowFilePicker(true);
              baseRef.current.startEditing();
            }}
          >
            <PlusMediumIcon />
          </button>
        )}
        {currentValue &&
          Array.isArray(currentValue) &&
          currentValue.map((file, i) => (
            <AttachmentItem
              file={file}
              key={i}
              onDelete={removeFile}
              readOnly={column.readOnly}
              actionPath={column.actionPath}
              onClick={() => {
                if (!column.actionPath) {
                  if (isFullScreen) {
                    setIsFullScreen();
                  }
                  filePreviewAPI.open(currentValue || []);
                  filePreviewAPI.setFileIndex(i);
                  if (customProps.flowType === FLOW_TYPE.PROCESS) {
                    filePreviewAPI.setPlugins([
                      FILEPREVIEW_PLUGINS.DOWNLOAD,
                      viewType === "Modal" &&
                        !formConfig?.fourColumn &&
                        FILEPREVIEW_PLUGINS.SPLITVIEW,
                      FILEPREVIEW_PLUGINS.PIP,
                      FILEPREVIEW_PLUGINS.NEWTAB,
                      FILEPREVIEW_PLUGINS.FULLSCREEN
                    ]);
                  }
                  filePreviewAPI.setNewTabURL(
                    `${formConfig.filePreviewNewTabURL}${column.Id}/${column.Model}/${rowIndex}`
                  );
                }
              }}
            />
          ))}
        {showFilePicker && (
          <LazyLoadComponent
            component={() => import("kfplatform/filePicker")}
            componentName="FilePicker"
            open={showFilePicker}
            onPickerClose={() => {
              setShowFilePicker(false);
            }}
            options={filePickerOptions}
            baseURL={column.uploadUrl}
            actionPath={column.actionPath}
            prefix={`${column.prefixUrl}/${row.id}`}
            onUploadDone={(value) => {
              baseRef.current.dataChanged([...value, ...(currentValue || [])]);
              baseRef.current.dataCompleted();
            }}
          />
        )}
      </div>
    </Base>
  );
}

Attachment.propTypes = {
  value: PropTypes.any,
  column: PropTypes.any,
  pos: PropTypes.any,
  row: PropTypes.any,
  rowIndex: PropTypes.any,
  setIsFullScreen: PropTypes.func,
  isFullScreen: PropTypes.bool,
  customProps: PropTypes.object,
  formConfig: PropTypes.object
};

function AttachmentItem(props) {
  const { file, onDelete, onClick, readOnly, actionPath } = props;
  return file.photos ? (
    <Tooltip content={file.name}>
      <div
        className={`${styles.thumb} ${actionPath ? styles.file : ""}`}
        style={
          !actionPath
            ? {
                backgroundImage: `url(/upload/2/${getS3FileKey(
                  file.photos[1].key
                )})`
              }
            : {}
        }
        onClick={onClick}
      >
        {!readOnly && (
          <button
            className={`${styles.close} shadow-6dp`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file);
            }}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </Tooltip>
  ) : (
    <Tooltip content={file.name}>
      <div
        className={`${styles.thumb} ${styles.file} ${
          styles[file.fileExtension]
        }`}
        onClick={onClick}
      >
        {!readOnly && (
          <button
            className={`${styles.close} shadow-6dp`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file);
            }}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </Tooltip>
  );
}

AttachmentItem.propTypes = {
  file: PropTypes.any,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  readOnly: PropTypes.bool,
  actionPath: PropTypes.string
};
